<script lang="ts" setup>
/**
 * トークルーム情報。
 */

import { defineProps, inject, provide, reactive, toRefs } from "vue";
import { CommunityTalkUserViewModel } from "@/viewModels/communityTalk/communityTalkUserViewModel";
import ApiGetLoadWithAddMaster from "@/components/common/api/ApiGetLoadWithAddMaster.vue";
import TalkRoomContents from "@/components/communityTalk/talkRoom/TalkRoomContents.vue";
import TalkRoomDate from "@/components/communityTalk/talkRoom/TalkRoomDate.vue";
import ReplyMaster from "@/components/communityTalk/reply/ReplyMaster.vue";
import ReplyOpenDetailsButton from "@/components/communityTalk/reply/ReplyOpenDetailsButton.vue";
import ReplyList from "@/components/communityTalk/reply/ReplyList.vue";
import ReplyMoreButton from "@/components/communityTalk/reply/ReplyMoreButton.vue";
import NoReply from "@/components/communityTalk/reply/NoReply.vue";
import ReplyPost from "@/components/communityTalk/reply/ReplyPost.vue";
import { ApiGetLoadAddStatusType } from "@/modules/types/apiLoadStatusType";
import CommunityTalkUser from "@/components/communityTalk/CommunityTalkUser.vue";
import CommunityTalkMedia from "@/components/common/CommunityTalkMedia.vue";
import TalkRoomNice from "@/components/communityTalk/talkRoom/TalkRoomNice.vue";
import { LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import ReplyController from "@/controllers/communityTalk/replyController";
import { ApiGetLoadAddStatus } from "@/modules/types/apiLoadStatusType";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { ReplyDto } from "@/apiRequests/models/communityTalk/replyGetResponse";
import { useReplyPostStore } from "@/store/communityTalk/replyPostStore";
import { TalkRoomDto } from "@/apiRequests/models/communityTalk/talkRoomGetResponse";

interface State {
	/**
	 * トークルームに紐づくリプライ。
	 */
	replies: ReplyDto[];
	/**
	 * リプライ追加取得時のAPIステータス。
	 */
	replyGetMoreStatus: ApiGetLoadAddStatusType;
}
const state = reactive<State>({
	replies: [],
	replyGetMoreStatus: "beforeRequest",
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const { replies, replyGetMoreStatus } = toRefs(state);

interface Props {
	/**
	 * トークルーム情報。
	 */
	talkRoom: TalkRoomDto;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
}
const props = defineProps<Props>();

const communityTalkUserViewModel: CommunityTalkUserViewModel = {
	badgeType: null,
	nickName: props.talkRoom.nickName,
	profilePicUrl: props.talkRoom.profilePicUrl,
	reviewerAttributeAnswers: props.talkRoom.reviewerAttributeAnswers,
};

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

// リプライ投稿用Store生成
// inject用のKeyを生成
const replyPostStoreKey = props.talkRoom.talkRoomId + "_replyPostStore";
// Storeを生成
const replyPostStore = useReplyPostStore();
// Storeを親コンポーネントから供給
provide(replyPostStoreKey, replyPostStore);
// replyPostStoreの初期データを登録
replyPostStore.init(props.talkRoom.talkRoomId);

/**
 * リプライ追加表示ボタンを表示するかどうか。
 */
const isShowReplyMoreButton = (): boolean => {
	return props.talkRoom.replyCount > state.replies.length;
};

/**
 * 詳細表示モードかどうか。
 */
const isDetailsMode = (): boolean => {
	// トークルーム情報が保持するリプライ詳細が0件の場合=詳細表示モードではないと判断
	if (state.replies.length === 0) {
		return false;
	}
	return true;
};

/**
 * 詳細表示モードを終了する。
 */
const closeDetails = (): void => {
	state.replies = [];
};

/**
 * リプライ追加APIリクエスト中かどうか。
 */
const isProcessingGetMoreReply = (): boolean => {
	return state.replyGetMoreStatus === ApiGetLoadAddStatus.loading || state.replyGetMoreStatus === ApiGetLoadAddStatus.addLoading;
};

const replyController = new ReplyController();
/**
 * リプライを追加表示する。
 */
const getMoreReply = async (): Promise<void> => {
	if (isProcessingGetMoreReply()) return;
	state.replyGetMoreStatus = ApiGetLoadAddStatus.loading;

	let sortKey: string | null = null;
	if (state.replies.length > 0) {
		const lastReply = state.replies[state.replies.length - 1];
		sortKey = lastReply.replySortKey;
	}

	try {
		// APIリクエスト
		const replyResponse = await replyController.getReplyData(
			communityTalkStore.getGeneralSettings().replyInitialDisplayCnt,
			communityTalkStore.getGeneralSettings().replyAdditionalDisplayCnt,
			props.talkRoom.talkRoomId,
			// 紐づくリプライが0件(=1回もトークルーム詳細ボタンが押されていない)であれば、初期表示モードとして連携
			state.replies.length === 0,
			sortKey,
			communityTalkStore.getTenantId(),
			communityTalkStore.getSubjectCd()
		);
		// データを格納
		state.replies.push(...replyResponse.data);
		state.replyGetMoreStatus = ApiGetLoadAddStatus.success;
	} catch (e) {
		state.replyGetMoreStatus = ApiGetLoadAddStatus.error;
		console.error(e);
	}
};
</script>

<template>
	<div class="revico-community-talk-talkroom-details">
		<div class="revico-community-talk-talkroom-details-contents">
			<CommunityTalkUser
				:view-model="communityTalkUserViewModel"
				:post-date="talkRoom.talkRoomDate"
				:new-arrival-badge-display-days="communityTalkStore.getGeneralSettings().newArrivalBadgeDisplayDays"
				:new-arrival-badge-text="communityTalkStore.getWordSettings().newArrivalBadgeText"
			/>
			<TalkRoomContents :contents="talkRoom.contents" />
			<CommunityTalkMedia :images="props.talkRoom.images" :movies="props.talkRoom.movies" />
			<TalkRoomNice :niceCount="talkRoom.goodCount" :communityTalkStoreKey="communityTalkStoreKey" :talkRoomId="talkRoom.talkRoomId" />

			<TalkRoomDate :talkRoomDate="talkRoom.talkRoomDate" />
		</div>

		<!-- リプライが1件でもある場合 -->
		<template v-if="props.talkRoom.replyCount !== 0">
			<!-- 概要表示時 -->
			<template v-if="!isDetailsMode()">
				<!-- ベストアンサー -->
				<ReplyMaster
					v-if="talkRoom.bestAnswerReply"
					:reply="talkRoom.bestAnswerReply"
					:wordSettings="wordSettings"
					:communityTalkStoreKey="communityTalkStoreKey"
					:replyPostStoreKey="replyPostStoreKey"
				/>

				<!-- リプライ総数(詳細表示ボタン) -->
				<ReplyOpenDetailsButton
					v-if="!isDetailsMode() && props.talkRoom.replyCount > 1"
					:replyCount="talkRoom.replyCount"
					:replyMoreDisplayText="wordSettings.replyMoreDisplayText"
					:isProcessingGetMoreReply="isProcessingGetMoreReply()"
					@on-click-more-button="getMoreReply"
				/>

				<!-- リプライ投稿フォーム -->
				<!-- ベストアンサー1件のみの表示の場合、投稿フォームがここに表示 -->
				<ReplyPost :communityTalkStoreKey="communityTalkStoreKey" :replyPostStoreKey="replyPostStoreKey" :wordSettings="wordSettings" />
			</template>

			<!-- 詳細表示: リプライ追加表示 -->
			<template v-else>
				<api-get-load-with-add-master :status="state.replyGetMoreStatus" v-if="isDetailsMode()">
					<template #contents>
						<ReplyList :replies="replies" :wordSettings="wordSettings" :communityTalkStoreKey="communityTalkStoreKey" :replyPostStoreKey="replyPostStoreKey" />

						<!-- リプライ追加表示ボタン -->
						<ReplyMoreButton
							v-if="isShowReplyMoreButton()"
							:moreButtonDisplayName="wordSettings.moreButtonDisplayName"
							:isProcessingGetMoreReply="isProcessingGetMoreReply()"
							@on-click-more-button="getMoreReply"
						/>

						<!-- リプライ詳細表示閉じるボタン -->
						<button type="button" class="revico-community-talk-talkroom-details--details-close-button" @click="closeDetails">
							<font-awesome-icon icon="fa-solid fa-angle-down" />
							閉じる
						</button>

						<!-- リプライ投稿フォーム -->
						<ReplyPost :communityTalkStoreKey="communityTalkStoreKey" :replyPostStoreKey="replyPostStoreKey" :wordSettings="wordSettings" />
					</template>
				</api-get-load-with-add-master>
			</template>
		</template>

		<!-- リプライ0件時 -->
		<div v-else class="revico-community-talk-talkroom-details-no-reply">
			<!-- リプライが存在しない表示 -->
			<NoReply :noReplyText="wordSettings.noReplyText" />
			<!-- リプライ投稿フォーム -->
			<ReplyPost :communityTalkStoreKey="communityTalkStoreKey" :replyPostStoreKey="replyPostStoreKey" :wordSettings="wordSettings" />
		</div>
	</div>
</template>

<style lang="scss">
.revico-widget .revico-community-talk {
	&-talkroom-details {
		&-contents {
			margin-bottom: 12px;
		}

		&--details-close-button {
			@include resetButton;
			display: block;
			width: 100%;
			margin: 24px 0 0 0;
			text-align: left;
		}

		&-no-reply {
			padding: 16px;
			background-color: var(--reply-bg-color);
		}
	}
}
</style>
